/**
 * Share design modal — short code (CHDIY12345678) via cloud API or local fallback.
 */
(function (global) {
  const Core = () => global.BraceletCore;
  const Cloud = () => global.DesignCloudApi;

  const root = document.querySelector('[data-bd-share-modal]');
  if (!root) return;

  const els = {
    code: root.querySelector('[data-bd-share-code]'),
    copyBtn: root.querySelector('[data-bd-share-copy]'),
    copyLabel: root.querySelector('[data-bd-share-copy-label]'),
    closeBtns: root.querySelectorAll('[data-bd-share-close]'),
    title: root.querySelector('[data-bd-share-title]'),
    hint: root.querySelector('[data-bd-share-hint]'),
    note: root.querySelector('[data-bd-share-note]'),
    loading: root.querySelector('[data-bd-share-loading]'),
    loadingText: root.querySelector('[data-bd-share-loading-text]'),
    body: root.querySelector('[data-bd-share-body]'),
  };

  const cfgEl =
    document.getElementById('bracelet-designer-root') || document.getElementById('design-detail-root');
  const i18n = {
    title: cfgEl?.dataset.i18nShareModalTitle || 'Share design',
    hint:
      cfgEl?.dataset.i18nShareModalHint ||
      'Send the code below to a friend. They can tap Import Design on the design page to copy your bracelet.',
    copy: cfgEl?.dataset.i18nShareModalCopy || 'Copy code',
    copied: cfgEl?.dataset.i18nShareModalCopied || 'Code copied',
    note: cfgEl?.dataset.i18nShareModalNote || 'The code starts with CHDIY. Do not change any characters.',
    generating: cfgEl?.dataset.i18nShareModalGenerating || 'Generating share code…',
    close: cfgEl?.dataset.i18nShareModalClose || 'Close',
    empty: cfgEl?.dataset.i18nCodeCopyEmpty || 'Add beads before sharing',
    error: cfgEl?.dataset.i18nShareModalError || 'Could not create share code',
  };

  let currentDisplayCode = '';
  let toastFn = null;

  function showToast(message, opts = {}) {
    if (typeof toastFn === 'function') toastFn(message, opts);
  }

  function setLoading(active) {
    root.classList.toggle('is-loading', !!active);
    if (els.loading) els.loading.hidden = !active;
    if (els.body) els.body.hidden = !!active;
    if (active && els.loadingText) els.loadingText.textContent = i18n.generating;
    if (active && els.copyBtn) els.copyBtn.disabled = true;
    if (!active && els.copyBtn) els.copyBtn.disabled = false;
  }

  function openModalShell() {
    if (els.title) els.title.textContent = i18n.title;
    if (els.hint) els.hint.textContent = i18n.hint;
    if (els.note) els.note.textContent = i18n.note;
    if (els.copyLabel) els.copyLabel.textContent = i18n.copy;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bd-share-open');
    requestAnimationFrame(() => root.classList.add('is-visible'));
  }

  function openModalLoading() {
    currentDisplayCode = '';
    if (els.code) els.code.textContent = '';
    setLoading(true);
    openModalShell();
  }

  function openModal(displayCode) {
    currentDisplayCode = normalizeDisplayCode(displayCode);
    setLoading(false);
    if (els.code) els.code.textContent = currentDisplayCode;
  }

  function closeModal() {
    root.classList.remove('is-visible', 'is-loading');
    document.body.classList.remove('bd-share-open');
    setLoading(false);
    window.setTimeout(() => {
      root.hidden = true;
      root.setAttribute('aria-hidden', 'true');
      currentDisplayCode = '';
    }, 220);
  }

  async function copyText(text) {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
    const helper = document.createElement('textarea');
    helper.value = text;
    helper.setAttribute('readonly', '');
    helper.style.position = 'fixed';
    helper.style.left = '-9999px';
    document.body.appendChild(helper);
    helper.select();
    document.execCommand('copy');
    document.body.removeChild(helper);
  }

  function normalizeDisplayCode(raw) {
    const core = Core();
    if (!core) return String(raw || '');
    const digits =
      core.parseShortShareCode(raw) ||
      String(raw || '')
        .replace(/^CHDIY/i, '')
        .replace(/^Design/i, '')
        .replace(/[\u4e00-\u9fff]+/g, '')
        .replace(/\D/g, '')
        .slice(0, 8);
    if (!digits) return String(raw || '');
    return core.formatShareDisplayCode(digits);
  }

  async function createShareCode(payload) {
    const core = Core();
    if (!core) throw new Error('Core unavailable');

    const cloud = Cloud();
    if (cloud?.enabled?.()) {
      try {
        const result = await cloud.createShareCode(payload);
        if (result?.displayCode) return normalizeDisplayCode(result.displayCode);
      } catch (err) {
        if (!cloud.isCloudUnavailableError?.(err)) throw err;
      }
    }

    const local = core.registerLocalShareCode(payload);
    return normalizeDisplayCode(local.displayCode);
  }

  async function openShare(payload, options = {}) {
    toastFn = options.showToast || null;
    if (!payload?.designJson?.b?.length) {
      showToast(i18n.empty, { type: 'error' });
      return false;
    }
    openModalLoading();
    try {
      const displayCode = await createShareCode(payload);
      openModal(displayCode);
      return true;
    } catch {
      closeModal();
      showToast(i18n.error, { type: 'error' });
      return false;
    }
  }

  async function resolveShareInput(raw) {
    const core = Core();
    if (!core) return null;

    const parsed = core.parseDesignCode(raw);
    if (parsed?.designJson) return parsed;

    const digits = core.parseShortShareCode(raw);
    if (!digits) return null;

    const cloud = Cloud();
    if (cloud?.enabled?.()) {
      try {
        const remote = await cloud.resolveShareCode(digits);
        if (remote?.designJson) return remote;
      } catch (err) {
        if (!cloud.isCloudUnavailableError?.(err)) throw err;
      }
    }

    return core.resolveLocalShareCode(digits);
  }

  els.copyBtn?.addEventListener('click', async () => {
    if (!currentDisplayCode) return;
    try {
      await copyText(currentDisplayCode);
      if (els.copyLabel) els.copyLabel.textContent = i18n.copied;
      showToast(i18n.copied, { type: 'success' });
      window.setTimeout(() => {
        if (els.copyLabel) els.copyLabel.textContent = i18n.copy;
      }, 1800);
    } catch {
      showToast(i18n.copied, { type: 'success' });
    }
  });

  els.closeBtns?.forEach((btn) => btn.addEventListener('click', closeModal));

  document.addEventListener('keydown', (e) => {
    if (root.hidden || !root.classList.contains('is-visible')) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      closeModal();
    }
  });

  global.DesignShare = {
    openShare,
    resolveShareInput,
    closeModal,
  };
})(typeof window !== 'undefined' ? window : globalThis);
